import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BrandLogo } from "./BrandLogo";
import SidebarBackToTop from "./SidebarBackToTop";
import BackToTop from "./BackToTop";
import UserMenu from "./UserMenu";
import {
  PanelLeftClose, PanelLeftOpen,
  ChevronDown, ChevronRight,
  LayoutDashboard, FileBarChart2, AlertOctagon, Wrench,
  FileSignature, Route as RouteIcon, MessageSquareWarning, Bell, Menu,
} from "lucide-react";

const COLLAPSED_KEY = "iris_portal_sidebar_collapsed";
const REPORTS_OPEN_KEY = "iris_portal_reports_open";

const REPORT_CHILDREN = [
  { to: "/portal/reports/incidents",   label: "Incidents",   icon: AlertOctagon },
  { to: "/portal/reports/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/portal/reports/dar",         label: "DARs",        icon: FileSignature },
  { to: "/portal/reports/tour-scans",  label: "Tour Scans",  icon: RouteIcon },
];


export default function PortalLayout({ children }) {
  const { user } = useAuth();
  const loc = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch (err) { return false; }
  });
  const [reportsOpen, setReportsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(REPORTS_OPEN_KEY);
      if (stored === "0") return false;
      if (stored === "1") return true;
    } catch (err) { /* ignore */ }
    // Auto-expand when landing on any /portal/reports path
    return loc.pathname.startsWith("/portal/reports");
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch (err) { /* ignore */ }
  }, [collapsed]);
  useEffect(() => {
    try { localStorage.setItem(REPORTS_OPEN_KEY, reportsOpen ? "1" : "0"); } catch (err) { /* ignore */ }
  }, [reportsOpen]);

  // Auto-expand the Reports group whenever the user lands on (or navigates
  // into) a /portal/reports/* path — including drill-down from the dashboard
  // Portfolio Snapshots or programmatic navigation after initial mount.
  // Without this, a user who first landed on /portal and later drilled in
  // would find the Reports sub-nav collapsed and the sibling tabs hidden.
  useEffect(() => {
    if (loc.pathname.startsWith("/portal/reports")) setReportsOpen(true);
  }, [loc.pathname]);

  const widthClass = collapsed ? "w-16" : "w-60";
  const anyReportActive = loc.pathname.startsWith("/portal/reports");
  const navRef = useRef(null);

  // Preserve the portfolio drill-down (?portfolio=…&portfolio_name=…) across
  // the Reports sub-nav so a Client Admin who clicked into "South Region"
  // on the dashboard keeps that filter when switching between Incidents /
  // DAR / Maintenance / Tour Scans.
  const reportSearch = (() => {
    if (!anyReportActive) return "";
    const sp = new URLSearchParams(loc.search);
    const portfolio = sp.get("portfolio");
    if (!portfolio) return "";
    const next = new URLSearchParams();
    next.set("portfolio", portfolio);
    const pname = sp.get("portfolio_name");
    if (pname) next.set("portfolio_name", pname);
    return `?${next.toString()}`;
  })();

  // iter285 — mobile drawer state (same pattern as Layout.jsx)
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <div
        data-testid="portal-mobile-topbar"
        className="md:hidden fixed top-0 inset-x-0 h-14 z-40 flex items-center gap-3 px-4 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#27272A]"
      >
        <button
          data-testid="portal-mobile-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="p-2 -ml-2 text-white hover:text-[#FF3B30] transition-colors"
        >
          <Menu className="size-5" />
        </button>
        <Link to="/portal" className="flex items-center gap-2 flex-1 min-w-0">
          <BrandLogo variant="icon" height={28} />
          <span className="font-display font-black text-sm uppercase tracking-tighter truncate">Iris Portal</span>
        </Link>
      </div>
      {mobileOpen && (
        <button
          data-testid="portal-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        />
      )}
      <aside
        data-testid="portal-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`${widthClass} shrink-0 border-r border-[#27272A] flex flex-col bg-[#0A0A0A] sticky top-0 h-screen transition-[width,transform] duration-200
          max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-72
          ${mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"}`}
      >
        {/* Brand — iter280 clickable link to portal home */}
        <Link
          to="/portal"
          data-testid="portal-brand-home-link"
          aria-label="Return to portal home"
          className={`py-6 border-b border-[#27272A] flex items-center gap-3 group focus:outline-none focus:bg-[#141414] hover:bg-[#141414] transition-colors ${collapsed ? "px-3 justify-center" : "px-5"}`}
        >
          {collapsed ? (
            <BrandLogo variant="icon" height={32} className="group-hover:opacity-80 transition-opacity" />
          ) : (
            <>
              <BrandLogo variant="icon" height={36} className="group-hover:opacity-80 transition-opacity" />
              <div className="min-w-0">
                <div className="font-display font-black text-lg uppercase leading-none tracking-tighter group-hover:text-[#FF3B30] transition-colors">Iris Security</div>
                <div className="label-mute mt-1" data-testid="portal-client-name">{user?.client_name || "Client Portal"}</div>
              </div>
            </>
          )}
        </Link>

        {/* Collapse toggle */}
        <div className={`flex ${collapsed ? "justify-center" : "justify-end"} px-2 pt-2`}>
          <button
            data-testid="portal-sidebar-toggle"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#121212] transition-colors"
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>

        {/* Nav */}
        <div className="relative flex-1 min-h-0 flex flex-col">
          <nav
            ref={navRef}
            className="flex-1 py-2 overflow-y-auto min-h-0 sidebar-scroll"
            data-testid="portal-sidebar-nav"
          >
          {/* Dashboard */}
          <NavLink
            to="/portal"
            end
            data-testid="portal-nav-dashboard"
            title={collapsed ? "Dashboard" : undefined}
            className={({ isActive }) =>
              `flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-5"} py-3 text-sm tracking-wide transition-colors duration-150 border-l-2 ${
                isActive
                  ? "bg-[#121212] border-[#FF3B30] text-white"
                  : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
              }`
            }
          >
            <LayoutDashboard className="size-4 shrink-0" strokeWidth={2} />
            {!collapsed && <span className="uppercase font-medium">Dashboard</span>}
          </NavLink>

          {/* Reports group */}
          {collapsed ? (
            REPORT_CHILDREN.map((c) => (
              <NavLink
                key={c.to}
                to={`${c.to}${reportSearch}`}
                data-testid={`portal-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                title={c.label}
                className={({ isActive }) =>
                  `flex items-center justify-center px-0 py-3 text-sm transition-colors duration-150 border-l-2 ${
                    isActive
                      ? "bg-[#121212] border-[#FF3B30] text-white"
                      : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
                  }`
                }
              >
                <c.icon className="size-4 shrink-0" strokeWidth={2} />
              </NavLink>
            ))
          ) : (
            <div data-testid="portal-nav-group-reports">
              <div className={`w-full border-l-2 ${anyReportActive ? "border-[#FF3B30]" : "border-transparent"}`}>
                <button
                  type="button"
                  data-testid="portal-nav-reports-toggle"
                  onClick={() => setReportsOpen((v) => !v)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm tracking-wide transition-colors duration-150 ${
                    anyReportActive ? "text-white" : "text-zinc-400 hover:bg-[#121212] hover:text-white"
                  }`}
                >
                  <FileBarChart2 className="size-4 shrink-0" strokeWidth={2} />
                  <span className="uppercase font-medium flex-1 text-left">Reports</span>
                  {reportsOpen ? <ChevronDown className="size-3.5 text-zinc-500" /> : <ChevronRight className="size-3.5 text-zinc-500" />}
                </button>
              </div>
              {reportsOpen && (
                <div className="border-l-2 border-[#27272A] ml-5" data-testid="portal-nav-reports-children">
                  {REPORT_CHILDREN.map((c) => (
                    <NavLink
                      key={c.to}
                      to={`${c.to}${reportSearch}`}
                      data-testid={`portal-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 pl-4 pr-5 py-2 text-sm tracking-wide transition-colors duration-150 ${
                          isActive
                            ? "bg-[#121212] text-white"
                            : "text-zinc-400 hover:bg-[#121212] hover:text-white"
                        }`
                      }
                    >
                      <c.icon className="size-3.5 shrink-0" strokeWidth={2} />
                      <span>{c.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Complaints */}
          <NavLink
            to="/portal/complaints"
            data-testid="portal-nav-complaints"
            className={({ isActive }) =>
              `flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-5"} py-3 text-sm tracking-wide transition-colors duration-150 border-l-2 ${
                isActive
                  ? "bg-[#121212] border-[#FF3B30] text-white"
                  : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
              }`
            }
            title={collapsed ? "Complaints" : undefined}
          >
            <MessageSquareWarning className="size-4 shrink-0" strokeWidth={2} />
            {!collapsed && <span className="uppercase font-medium">Complaints</span>}
          </NavLink>

          {/* Notification settings */}
          <NavLink
            to="/portal/settings"
            data-testid="portal-nav-settings"
            className={({ isActive }) =>
              `flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-5"} py-3 text-sm tracking-wide transition-colors duration-150 border-l-2 ${
                isActive
                  ? "bg-[#121212] border-[#FF3B30] text-white"
                  : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
              }`
            }
            title={collapsed ? "Notification settings" : undefined}
          >
            <Bell className="size-4 shrink-0" strokeWidth={2} />
            {!collapsed && <span className="uppercase font-medium">Notifications</span>}
          </NavLink>
          </nav>
          <SidebarBackToTop targetRef={navRef} collapsed={collapsed} />
        </div>
      </aside>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <UserMenu />
        {children}
      </main>
      <BackToTop />
    </div>
  );
}
